export const CHECKOUT_URL = 'https://www.redondoland.com/checkout'

export const BRAND = 'REDONDOLAND'
export const PRODUCT = 'POOL CARE MADE EASY™'
export const PIXEL_CONTENT_NAME = 'POOL CARE MADE EASY'

export const MAIN_MOCKUP =
  'https://cdn.magicpatterns.com/uploads/kpk64b5aYt4Ma3p9gYRqGD/ChatGPT_Image_22_sept_2026,_21_25_09.png'

export const MOCKUP_ALT =
  'POOL CARE MADE EASY™ main guide surrounded by its 4 bonus guides: Robot Pool Cleaner, Manual Pool Cleaning, Pool Owner Shopping List and Pool Heating'

export const CLEAR_POOL_IMAGE =
  'https://cdn.magicpatterns.com/patterns/generated-images/3f19f012-d321-456a-a36c-e01312a69de6.jpg'

export const pricing = {
  regularPrice: '$97',
  today: '$14.99',
  todayValue: 14.99,
}

export const CTA_LABEL = 'Get Pool Care Made Easy Now →'

/* ---------------------------------------------------------------- Hero */

export const HERO_KICKER = 'The Pool Owner’s “What Do I Do Now?” Guide'

export const HERO_HEADLINE_TOP = 'Your pool has a problem.'
export const HERO_HEADLINE_BOTTOM = 'Now you’ll know what to do next.'

export const HERO_SUB =
  'Green water. Cloudy water. Dirt that won’t disappear. Chemicals you don’t understand. A heater you don’t know when to turn on.'

export const HERO_BODY =
  'POOL CARE MADE EASY™ gives you practical, step-by-step guidance for everyday pool problems — so you’re not left guessing or waiting for someone else to tell you what to do.'

export const heroFlow: string[] = ['See the problem', 'Check the cause', 'Know what to do next']

export const HERO_LABEL_SUB = '90-Page Practical Pool Owner Guide + 4 Bonus Guides'

/* Mobile-only short hero */
export const MOBILE_HERO_KICKER = 'POOL CARE MADE EASY™'
export const MOBILE_HERO_HEADLINE_TOP = 'Pool problem?'
export const MOBILE_HERO_HEADLINE_BOTTOM = 'Know what to do.'
export const MOBILE_HERO_SUB =
  'Green water, cloudy water, chemicals, cleaning & more — find what to check and what to do next.'
export const MOBILE_HERO_BUNDLE = '90-Page Guide + 4 Bonus Guides'
export const MOBILE_CTA_LABEL = 'Get Instant Access →'
export const mobileTrust: string[] = ['Instant Access', '7-Day Guarantee', '100% Digital']

export const heroBenefits: string[] = [
  'Find answers when something goes wrong',
  'Understand what to check before adding chemicals',
  'Solve routine pool problems with more independence',
  '4 practical bonus guides included',
  'Instant digital access',
  'Beginner-friendly',
]

/* ---------------------------------------------------------------- Pain */

export type PainCard = { title: string; lines: string[]; image: string; alt: string }

export const PAIN_HEADLINE = 'Has this ever happened to you?'

export const painCards: PainCard[] = [
  {
    title: 'You wake up and the water is green.',
    lines: ['Yesterday it looked fine.', 'Now what?'],
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/9a5b5e17-52d0-4617-883d-c5c2a677fe84.jpg',
    alt: 'Backyard pool with bright green algae water',
  },
  {
    title: 'The water is cloudy.',
    lines: ['You added products.', 'Nothing changed.'],
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/a12ac8e4-a262-4858-91ce-51a32523dccb.jpg',
    alt: 'Pool with cloudy, milky water',
  },
  {
    title: 'The pool guy can’t come.',
    lines: ['But you need the pool ready now.'],
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/acfcba27-cb0f-48fc-8ee6-d5b3a4c89b9f.jpg',
    alt: 'Pool with leaves floating and an unused skimmer net on the deck',
  },
  {
    title: 'You don’t know what to buy.',
    lines: ['Chlorine? Algaecide? Clarifier?', 'Something else?'],
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/1148df96-ed2d-4ecd-b88d-542a290af1c3.jpg',
    alt: 'Pool chemical containers and a test kit beside a bright blue pool',
  },
  {
    title: 'Dirt keeps coming back.',
    lines: ['You clean it. It returns.', 'Why?'],
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/339288cc-8bc2-40c4-bbd2-24ca2568bf93.jpg',
    alt: 'Dirt and leaves settled on a bright pool floor',
  },
  {
    title: 'You want the pool warm tomorrow.',
    lines: ['When do you turn the heater on?'],
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/01d327a8-679a-4c56-bc35-d59b8efe1251.jpg',
    alt: 'Pool heat pump beside a bright blue pool',
  },
]

export const PAIN_TRANSITION =
  'Pool ownership becomes frustrating when every small problem feels like a mystery.'
export const PAIN_TRANSITION_EMPHASIS = 'It doesn’t have to.'

/* ------------------------------------------------------------- Agitate */

export const AGITATE_HEADLINE = 'How much time and money have you already spent guessing?'

export const agitateStory: string[] = [
  'One person tells you to shock the pool.',
  'Another tells you to add algaecide.',
  'Someone else says it’s the filter.',
  'You search online and get five different answers.',
  'Then you buy another chemical hoping THIS is the one that fixes it.',
]

export const AGITATE_PUNCH = 'And sometimes the pool still looks exactly the same.'

export const agitateLines: string[] = [
  'Buying products before knowing what the problem actually is',
  'Adding chemicals because someone says you “probably need them”',
  'Searching different videos every time something changes',
  'Not knowing whether the problem is water chemistry, filtration, circulation or dirt',
  'Waiting for someone else to tell you what to do',
  'Worrying that you’ll make the problem worse',
  'Spending money without understanding why',
]

export const AGITATE_STATEMENT_TOP = 'You don’t need more random pool advice.'
export const AGITATE_STATEMENT_BOTTOM = 'You need to know what to do next.'

/* ----------------------------------------------------------- Questions */

export const QUESTIONS_HEADLINE =
  'The pool care knowledge you shouldn’t have to pay someone to explain every time.'

export const questionsBody: string[] = [
  'Most everyday pool problems aren’t mysterious.',
  'They only feel that way when nobody has shown you what to look for.',
]

export const ownerQuestions: string[] = [
  'Why did the water turn green?',
  'Why is it cloudy even after adding chemicals?',
  'Why does dirt keep returning to the floor?',
  'What should I test before buying another product?',
  'When might clarifier help?',
  'When might flocculant be considered?',
  'Why does chlorine seem to disappear?',
  'When should I heat the pool?',
  'How do I know how long heating may take?',
  'What can I safely handle myself?',
  'When do I actually need a professional?',
]

export const QUESTIONS_FINAL =
  'Once you know what to look for, pool care starts making much more sense.'

/* ------------------------------------------------------------ Mistakes */

export type Mistake = { title: string; body: string }

export const MISTAKES_HEADLINE =
  '3 costly pool mistakes you may be making without realizing it'

export const mistakes: Mistake[] = [
  {
    title: 'Treating the color instead of identifying the cause.',
    body: 'Green, cloudy, white or brown water doesn’t automatically mean the same solution every time.',
  },
  {
    title: 'Buying a product before checking what your pool actually needs.',
    body: 'Testing and identifying the problem comes before choosing what to add.',
  },
  {
    title: 'Waiting until something goes wrong to figure out what to do.',
    body: 'That’s when you’re rushed, frustrated and more likely to guess.',
  },
]

export const MISTAKES_STATEMENT_TOP = 'You don’t need to become a pool expert.'
export const MISTAKES_STATEMENT_BOTTOM = 'You just need the right answer when you need it.'

/* ------------------------------------------------------ Product reveal */

export const REVEAL_TITLE = 'Meet your pool’s “What do I do now?” guide.'

export const REVEAL_BODY =
  'POOL CARE MADE EASY™ is a 90-page practical guide designed to help you understand what’s happening, identify what to check, and choose the appropriate next step.'

export const REVEAL_OLD_QUESTION = '“What chemical should I add?”'
export const REVEAL_NEW_QUESTION = '“What is actually causing this?”'

export const processSteps: string[] = [
  'Look',
  'Test',
  'Identify',
  'Treat',
  'Clean & Filter',
  'Retest',
]

export const PROCESS_TITLE = 'One repeatable process.'
export const PROCESS_BODY =
  'Whether the water turns green, cloudy or strange — start with the same logic instead of random guessing.'

/* ------------------------------------------------------ Problem finder */

export type ProblemGroup = { group: string; swatch: string; problems: string[] }

export const FINDER_HEADLINE = 'Just look at what’s wrong — and start there.'

export const problemGroups: ProblemGroup[] = [
  {
    group: 'Water color & clarity',
    swatch: '#3fbf7f',
    problems: [
      'Green water',
      'Cloudy water',
      'White or milky water',
      'Brown / orange / rust-colored water',
      'Floating particles',
    ],
  },
  {
    group: 'Growth & surfaces',
    swatch: '#d4b26a',
    problems: [
      'Green algae',
      'Yellow / mustard material',
      'Black spots',
      'Slippery walls or floor',
      'White rough deposits',
      'Stains',
    ],
  },
  {
    group: 'Comfort & chemistry',
    swatch: '#2ee6d6',
    problems: [
      'Strong chlorine smell',
      'Eye irritation',
      'Foam',
      'Chlorine disappearing',
      'pH changing',
    ],
  },
  {
    group: 'Situations',
    swatch: '#1e9bff',
    problems: [
      'Dirt returning to the floor',
      'Heavy rain',
      'Heavy swimmer use',
      'Neglected pool',
    ],
  },
]

export const finderSteps: string[] = [
  'Find the symptom.',
  'See what it may mean.',
  'Know what to check next.',
]

/* ------------------------------------------------------------- Inside */

export const INSIDE_HEADLINE = 'From “I have no idea” to “I know what to check.”'

export type InsideGroup = { group: string; items: string[] }

export const insideGroups: InsideGroup[] = [
  {
    group: 'Your pool system',
    items: [
      'Calculate your pool’s water volume',
      'Understand your pump',
      'Understand circulation',
      'Understand your filter',
    ],
  },
  {
    group: 'Your water',
    items: [
      'Understand chlorine',
      'Understand pH',
      'Understand alkalinity',
      'Understand stabilizer / CYA',
      'Understand calcium hardness',
      'Test your water',
      'Understand your results',
      'Saltwater pool basics',
    ],
  },
  {
    group: 'Cleaning',
    items: ['Manual, automatic and robotic cleaning', 'Pool cleaning tools'],
  },
  {
    group: 'Treatments',
    items: [
      'Shock',
      'Algaecide',
      'Clarifier',
      'Flocculant',
      'Chemical dosing principles',
      'Chemical safety',
    ],
  },
  {
    group: 'Routine care',
    items: ['Daily care', 'Weekly care', 'Monthly care', 'Seasonal care', 'Quick-reference tools'],
  },
]

export const INSIDE_CLOSING_TOP = 'Not information for the sake of information.'
export const INSIDE_CLOSING_BOTTOM = 'Information you can USE when your pool needs you.'

/* ------------------------------------------------------------ Carousel */

export type EbookPage = { image: string; alt: string }

export const CAROUSEL_TITLE = 'See what’s inside'

export const CAROUSEL_SUB = 'Real pages from POOL CARE MADE EASY™ and the included guides.'

export const CAROUSEL_STATEMENT = 'Keep the answer on your phone.'

/** Real pages from the product — used exactly as provided. */
export const ebookPages: EbookPage[] = [
  {
    image: 'https://cdn.magicpatterns.com/uploads/xjrDEhPQNcTEwxZfaX7A3R/c3fc5518-2827-4a70-b31a-c0b138e7fa7f.png',
    alt: 'Guide page: Part 2 — What’s wrong with my pool?',
  },
  {
    image: 'https://cdn.magicpatterns.com/uploads/9vcKHrFGxKa6Cc5YktdzkN/c52184cf-6016-4255-a716-f11f21a76958.png',
    alt: 'Guide page: Quick Problem Index',
  },
  {
    image: 'https://cdn.magicpatterns.com/uploads/gmEYk4po5qnYGnmE36ax1c/02317939-9b36-49eb-bcb5-6b312ec9467c.png',
    alt: 'Guide page: Quick Problem Index, continued',
  },
  {
    image: 'https://cdn.magicpatterns.com/uploads/oSxEDdk1npeFTsZ9mF1Qpx/2c7439be-9b99-4251-ac36-4cbbdcf69fe1.png',
    alt: 'Guide page: Calculate your pool volume',
  },
  {
    image: 'https://cdn.magicpatterns.com/uploads/1szcWJfs5wFKqVXg5PqV4A/876e7d0c-4540-4f66-9f4a-cbb950a33c1f.png',
    alt: 'Guide page: Algaecide vs. clarifier vs. flocculant',
  },
  {
    image: 'https://cdn.magicpatterns.com/uploads/u1QqsGAyRFwARf86sMumEx/31a1fa45-9d71-4561-9ba9-a785bf5d998e_(1).png',
    alt: 'Guide page: Why do my eyes burn? Why is my pool foaming?',
  },
  {
    image: 'https://cdn.magicpatterns.com/uploads/qwFG1g3fmNYi5Wv9ebFqUo/bbb661e9-2896-44d0-8fb0-3abb204ac97d.png',
    alt: 'Guide page: My pool has been neglected. Where do I start?',
  },
  {
    image: 'https://cdn.magicpatterns.com/uploads/mhNSNtEpLCTCN8pZHZsCZV/37da042e-482b-4a92-a073-68181cca0455.png',
    alt: 'Guide page: What do I actually need to clean my pool?',
  },
]

/* ------------------------------------------------------------- Bonuses */

export type Bonus = {
  number: string
  title: string
  subtitle: string
  benefits: string[]
  value: string
  image: string
}

export const bonuses: Bonus[] = [
  {
    number: '1',
    title: 'Robot Pool Cleaner Made Easy™',
    subtitle:
      'Know how to use, care for and troubleshoot your robotic cleaner with more confidence.',
    benefits: [
      'Setup and use',
      'Cleaning routines',
      'Filters and baskets',
      'Common performance problems',
      'Routine care and storage',
    ],
    value: '$27',
    image: 'https://cdn.magicpatterns.com/uploads/sQqb9XfpKkgVABdUpEr7pC/PAG_BONO_1.png',
  },
  {
    number: '2',
    title: 'Manual Pool Cleaning Made Easy™',
    subtitle:
      'Know how to clean your pool even when you don’t have — or don’t want to depend on — a robot.',
    benefits: [
      'Essential cleaning tools',
      'Skimming',
      'Brushing',
      'Manual vacuuming',
      'Cleaning order',
      'Equipment care',
    ],
    value: '$27',
    image: 'https://cdn.magicpatterns.com/uploads/tnZAtb6Phq8vzxnx39zctH/PAG_BONO_2.png',
  },
  {
    number: '3',
    title: 'Pool Owner Shopping List™',
    subtitle: 'Stop standing in the pool store wondering what you actually need.',
    benefits: [
      'Essential pool products',
      'Testing supplies',
      'Cleaning tools',
      'Chemical categories',
      'Useful upgrades',
      'Problem-specific products',
    ],
    value: '$19',
    image: 'https://cdn.magicpatterns.com/uploads/u9Yvod56UXW3rEkAfhK3Mq/PAG_BONO_3.png',
  },
  {
    number: '4',
    title: 'Pool Heating Made Easy™',
    subtitle: 'Stop guessing when to turn your pool heater on.',
    benefits: [
      'Pool volume',
      'Current vs target temperature',
      'Heater output',
      'Heating-time estimation',
      'Heat loss',
      'Covers',
      'Cost estimation',
    ],
    value: '$27',
    image: 'https://cdn.magicpatterns.com/uploads/gXMgvfMhgv32t8MNpxcNMt/PAG_BONO_4.png',
  },
]

export const BONUS_TOTAL = '$100'

/* --------------------------------------------------------------- Offer */

export const offerItems: { title: string; note?: string }[] = [
  { title: 'Pool Care Made Easy™', note: '90-page main guide' },
  { title: 'Robot Pool Cleaner Made Easy™', note: 'Bonus guide' },
  { title: 'Manual Pool Cleaning Made Easy™', note: 'Bonus guide' },
  { title: 'Pool Owner Shopping List™', note: 'Bonus guide' },
  { title: 'Pool Heating Made Easy™', note: 'Bonus guide' },
]

/* ---------------------------------------------------------- Comparison */

export type ComparisonRow = { aspect: string; without: string; with: string }

export const comparisonRows: ComparisonRow[] = [
  {
    aspect: 'Pool turns green',
    without: 'Search online. Ask around. Start guessing.',
    with: 'Find the symptom and begin with the appropriate checks.',
  },
  {
    aspect: 'Pool is cloudy',
    without: 'Try another chemical.',
    with: 'Check possible causes before choosing the next action.',
  },
  {
    aspect: 'Pool store',
    without: '“What should I buy?”',
    with: 'Know your pool volume, test results and actual problem.',
  },
  {
    aspect: 'Cleaning',
    without: 'Clean randomly and hope it stays clean.',
    with: 'Know which tool and method fits the job.',
  },
  {
    aspect: 'Heating',
    without: 'Turn it on and hope it’s warm in time.',
    with: 'Estimate heating needs using your pool and equipment information.',
  },
  {
    aspect: 'Everyday problems',
    without: '“Should I call someone?”',
    with: 'Know what you can handle — and when professional help makes sense.',
  },
]

/* -------------------------------------------------------- Independence */

export const INDEPENDENCE_HEADLINE =
  'You don’t need to call someone for every question your pool creates.'

export const independenceBody: string[] = [
  'There will always be situations that require a qualified professional.',
  'But many everyday pool questions begin with things an informed owner can observe, test, clean, check or understand.',
]

export const INDEPENDENCE_GOAL_NOT = 'The goal isn’t to turn you into a pool technician.'
export const INDEPENDENCE_GOAL = 'The goal is to stop feeling helpless every time something changes.'

export const independenceStatement: string[] = ['Your pool.', 'Your guide.', 'Your next step.']

/* ----------------------------------------------------------- Guarantee */

export const guaranteeSteps: string[] = [
  'Open the guide.',
  'Explore the problem finder.',
  'Look through the maintenance system.',
  'Check the bonus guides.',
]

/* ----------------------------------------------------------------- FAQ */

export type Faq = { question: string; answer: string }

export const faqs: Faq[] = [
  {
    question: 'I’m a complete beginner. Is this for me?',
    answer:
      'Yes. The guides are designed for regular pool owners, not pool technicians. The explanations are simple, practical and step-by-step.',
  },
  {
    question: 'Do I need to read all 90 pages before using it?',
    answer:
      'No. You can learn the fundamentals, but the guide is also designed as a reference. When something happens, use the problem sections and Quick Reference tools to find what you need.',
  },
  {
    question: 'Will this tell me what to do if my pool turns green or cloudy?',
    answer:
      'The guide helps you understand the possible causes, what to check and test, and how to determine the appropriate next step instead of treating every color change the same way.',
  },
  {
    question: 'Does it tell me exactly how much chemical to add?',
    answer:
      'Chemical dosing depends on your pool volume, current test results, target change, product formulation and concentration. The guide teaches you how to approach dosing correctly and follow the specific product label rather than using unsafe universal doses.',
  },
  {
    question: 'Can I use it for a saltwater pool?',
    answer:
      'Yes. Saltwater pool owners will find relevant information inside. Equipment-specific instructions should always follow the manufacturer’s manual.',
  },
  {
    question: 'Does this replace a pool professional?',
    answer:
      'No. It helps you become a more informed and independent pool owner for routine care and common problems. Electrical, gas, structural, major plumbing, serious equipment and unknown chemical hazards require qualified professional assistance.',
  },
  {
    question: 'How do I receive everything?',
    answer:
      'The product is 100% digital. You’ll receive digital access to the main guide and included bonus guides after purchase.',
  },
  {
    question: 'Can I use it on my phone?',
    answer:
      'Yes. Keep the guides on a compatible phone, tablet or computer so they’re available when you need them.',
  },
  {
    question: 'Why not just search online?',
    answer:
      'Because when something goes wrong, you don’t need twenty conflicting opinions. You need an organized process that helps you understand what you’re seeing, what to check and what to do next.',
  },
]

/* --------------------------------------------------------- Final close */

export const FINAL_KICKER = 'The next pool problem isn’t going to schedule itself.'
export const FINAL_HEADLINE = 'When it happens, have the answer within reach.'

export const finalMaybes: string[] = [
  'Maybe it’ll be green water.',
  'Maybe it’ll be cloudy water.',
  'Maybe dirt won’t disappear.',
  'Maybe you’ll need the pool warm tomorrow.',
  'Maybe you’ll be standing in the pool store wondering which product you actually need.',
]

export const FINAL_BODY =
  'Whatever happens, owning a pool feels very different when your first thought is no longer:'
export const FINAL_QUOTE = '“What do I do now?”'

/* -------------------------------------------------------------- Footer */

export const FOOTER_TAGLINE = 'Practical ideas. Clear solutions.'

export const FOOTER_DISCLAIMER =
  'POOL CARE MADE EASY™ is educational material. Pool systems, surfaces, equipment, chemical formulations and local requirements vary. Always follow product labels, equipment manufacturer instructions and applicable local requirements. Seek qualified professional assistance for electrical, gas, structural, major plumbing, serious equipment or unknown chemical hazards.'
